import React from 'react';
import { Play, Pause, SkipBack, Music2, Layers, Download, RefreshCw, Edit } from 'lucide-react';
import { SoundSignalVisualizer, SoundSignalVisualizerRef } from './SoundSignalVisualizer';

interface StickyPlayerProps {
    title: string;
    description?: string;
    isPlaying: boolean;
    onTogglePlay: () => void;
    onRewind: () => void;
    audioUrl?: string;
    bpm: number;
    bpmInput: string;
    bpmAuto: boolean;
    onBpmAutoChange: (value: boolean) => void;
    onBpmDecrease: () => void;
    onBpmIncrease: () => void;
    onBpmInputChange: (value: string) => void;
    onBpmInputBlur: () => void;
    hasSoundworld: boolean;
    hasAudio: boolean;
    generating: boolean;
    stemsLoading: boolean;
    onGenerate: () => void;
    onGenerateAgain: () => void;
    onGetMidi: () => void;
    onGetStems: () => void;
    onDownloadMp3: () => void;
    audioRef: React.RefObject<SoundSignalVisualizerRef>;
    soundworldName?: string;
    chords: string;
    onChordsChange: (value: string) => void;
    onOpenChordEditor: () => void;
    sections?: Array<{ start: number; end: number; label: string }>;
}

export const StickyPlayer: React.FC<StickyPlayerProps> = ({
    title,
    description,
    isPlaying,
    onTogglePlay,
    onRewind,
    audioUrl,
    bpm,
    bpmInput,
    bpmAuto,
    onBpmAutoChange,
    onBpmDecrease,
    onBpmIncrease,
    onBpmInputChange,
    onBpmInputBlur,
    hasSoundworld,
    hasAudio,
    generating,
    stemsLoading,
    onGenerate,
    onGenerateAgain,
    onGetMidi,
    onGetStems,
    onDownloadMp3,
    audioRef,
    soundworldName,
    chords,
    onChordsChange,
    onOpenChordEditor,
    sections,
}) => {
    return (
        <div className="fixed bottom-0 left-0 right-0 md:left-56 z-40 bg-background/95 backdrop-blur border-t border-border">
            <div className="px-3 md:px-6 py-3 flex flex-col gap-2">
                <div className="flex flex-col gap-2">
                    {audioUrl ? (
                        <SoundSignalVisualizer
                            ref={audioRef}
                            audioUrl={audioUrl}
                            barsCount={6}
                            showPlaybackControls
                            showBpmControls={false}
                            showExtraControls={false}
                            compact
                            sections={sections}
                        />
                    ) : (
                        <div className="bg-card rounded-lg border border-border p-3">
                            <div className="h-[111px] flex items-center justify-center text-xs text-muted-foreground">
                                No audio yet
                            </div>
                        </div>
                    )}
                    <div className="flex flex-col md:flex-row md:items-center gap-2 mt-1">
                        <div className="flex items-center gap-2 flex-shrink-0">
                            <div className="flex items-center gap-1.5">
                                <input
                                    type="checkbox"
                                    id="sticky-bpm-auto"
                                    checked={bpmAuto}
                                    onChange={(e) => onBpmAutoChange(e.target.checked)}
                                    className="w-3.5 h-3.5 text-card-foreground border-muted rounded focus:ring-primary"
                                />
                                <label htmlFor="sticky-bpm-auto" className="text-[11px] font-medium text-muted-foreground">
                                    Auto
                                </label>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <button
                                    onClick={onBpmDecrease}
                                    disabled={bpmAuto}
                                    className={`px-2 text-xs rounded border border-border text-muted-foreground hover:text-card-foreground hover:bg-accent ${bpmAuto ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    -
                                </button>
                                <input
                                    type="number"
                                    value={bpmInput}
                                    onChange={(e) => onBpmInputChange(e.target.value)}
                                    onBlur={onBpmInputBlur}
                                    disabled={bpmAuto}
                                    className={`w-14 text-center text-xs font-medium border border-muted rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary ${bpmAuto ? 'opacity-50 cursor-not-allowed bg-muted' : ''}`}
                                    min={60}
                                    max={200}
                                />
                                <span className="text-[11px] font-medium text-muted-foreground">BPM</span>
                                <button
                                    onClick={onBpmIncrease}
                                    disabled={bpmAuto}
                                    className={`px-2 text-xs rounded border border-border text-muted-foreground hover:text-card-foreground hover:bg-accent ${bpmAuto ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    +
                                </button>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                            <span className="text-[11px] font-medium text-muted-foreground whitespace-nowrap">
                                Chord progression
                            </span>
                            <div className="relative flex-1 min-w-[120px]">
                                <input
                                    type="text"
                                    value={chords}
                                    onChange={(e) => onChordsChange(e.target.value)}
                                    className="w-full px-3 py-1.5 pr-7 border border-muted rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background text-card-foreground placeholder:text-muted-foreground text-xs"
                                    placeholder="Type your chord progression or use the editor"
                                />
                                <button
                                    onClick={onOpenChordEditor}
                                    className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-card-foreground"
                                    title="Open progression editor"
                                >
                                    <Edit size={13} />
                                </button>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 overflow-x-auto pb-1 md:pb-0 md:pl-2 flex-shrink-0">
                            {hasAudio ? (
                                <>
                                    <button
                                        onClick={onGetMidi}
                                        className="btn-primary px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 whitespace-nowrap"
                                    >
                                        <Music2 size={14} />
                                        MIDI
                                    </button>
                                    <button
                                        onClick={onGetStems}
                                        disabled={stemsLoading}
                                        className="btn-primary px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {stemsLoading ? (
                                            <>
                                                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                Stems...
                                            </>
                                        ) : (
                                            <>
                                                <Layers size={14} />
                                                Stems
                                            </>
                                        )}
                                    </button>
                                    <button
                                        onClick={onDownloadMp3}
                                        className="btn-primary px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 whitespace-nowrap"
                                    >
                                        <Download size={14} />
                                        Audio
                                    </button>
                                    <button
                                        onClick={onGenerateAgain}
                                        disabled={!hasSoundworld || generating}
                                        className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 whitespace-nowrap ${hasSoundworld ? 'btn-primary' : 'bg-muted text-muted-foreground cursor-not-allowed'}`}
                                    >
                                        <RefreshCw size={14} />
                                        {generating ? 'Generating...' : 'Again'}
                                    </button>
                                </>
                            ) : (
                                <button
                                    onClick={onGenerate}
                                    disabled={!hasSoundworld || generating}
                                    className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 whitespace-nowrap ${hasSoundworld ? 'btn-primary' : 'bg-muted text-muted-foreground cursor-not-allowed'}`}
                                >
                                    <RefreshCw size={14} />
                                    {generating ? 'Generating...' : 'Generate'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};


